import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Tooltip
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

const steps = [
  {
    title: "Set Up the Channel and Schedule",
    images: [
      "/assets/step1.jpg",
      "/assets/step1.5.jpg",
    ],
    points: [
      "Go to the video repository and click on Live Channels.",
      "Enable the live class channel by turning on the toggle button.",
      "Copy the Playback Key and paste it into the batch schedule under the 'Stream Key' option. Then create the live class schedule.",
      "Copy the Stream Key from the live class channel page.",
    ],
  },
  {
    title: "Set Up OBS for Streaming",
    images: [
      "/assets/step2.jpg",
      "/assets/step2.5.jpg",
    ],
    points: [
      "Open OBS and go to Preferences, then select the Stream tab.",
      "In the Service option, select 'Mux'.",
      "In the Server option, choose Global (RTMPS).",
      "Paste the copied Stream Key into the Stream Key field in OBS.",
      "Click Apply at the bottom.",
      "Click OK to exit the Preferences window.",
    ],
  },
  {
    title: "Start the Live Class in OBS",
    images: [
      "/assets/step3.jpg",
    ],
    points: [
      "In the Sources section, make sure your camera / video capture device is selected",
      "Select the source, then click Start Streaming on the right side",
      "Click Start Recording to begin recording the session.",
      "Check the application dashboard to confirm that the live class has started successfully.",
    ],
  },
];

const LiveClassGuideModal = () => {
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ src: string; stepTitle: string } | null>(null);


  return (
    <>
      <Tooltip title="Live Class Guide">
        <IconButton
          onClick={() => setOpen(true)}
          aria-label="Open Live Class Guide"
          sx={{
            ml: 1,
            backgroundColor: "#f5f5f5",
            "&:hover": { backgroundColor: "#e0e0e0" },
          }}
        >
          <InfoOutlinedIcon />
        </IconButton>
      </Tooltip>

      {/* 🔹 Modal */}
      <Dialog 
        open={open} 
        onClose={() => {
          setOpen(false);
          setSelectedImage(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <Dialog
          open={Boolean(selectedImage)}
          onClose={() => setSelectedImage(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
            Image Preview
            <IconButton 
              onClick={() => setSelectedImage(null)}
              aria-label="Close image preview"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ textAlign: "center" }}>
            {selectedImage && (
              <Box
                component="img"
                src={selectedImage.src}
                alt={selectedImage.stepTitle}
                sx={{
                  width: "100%",
                  height: "auto",
                  borderRadius: 2,
                }}
              />
            )}
          </DialogContent>
        </Dialog>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          🎥 Live Class Setup Guide
        </DialogTitle>

        <DialogContent dividers>
          <Stepper orientation="vertical" activeStep={-1}>
            {steps.map((step, index) => (
              <Step key={index} expanded>
                <StepLabel>
                  <Typography variant="h6" fontWeight="600">
                    {step.title}
                  </Typography>
                </StepLabel>

                <Card sx={{ mb: 3, ml: 2, borderRadius: 3 }}>
                  <CardContent>
                    {/* Images */}
                    <Grid container spacing={2} mb={2}>
                      {step.images.map((img, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Box
                            component="img"
                            src={img}
                            alt={`${step.title} - Image ${idx + 1}`}
                            onClick={() => setSelectedImage({ src: img, stepTitle: step.title })}
                            sx={{
                              width: "100%",
                              aspectRatio: "16/9",
                              objectFit: "contain",
                              borderRadius: 2,
                              cursor: "pointer",
                              transition: "0.2s",
                              "&:hover": {
                                transform: "scale(1.02)",
                              },
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>

                    {/* Points */}
                    {step.points.map((point, i) => (
                      <Typography key={i} sx={{ mb: 1 }}>
                        {i + 1}. {point}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LiveClassGuideModal;